import { parseCsClasses } from '../CsImport/CsClassParser';
import { walkCallChain } from './CsSeqOrchestrator';
import { buildSequenceShape } from './CsSeqShapeFactory';
import { buildMermaidSequence } from './CsSeqMermaidBuilder';

/**
 * Parses multiple C# file sources for sequence-diagram use. Unlike the
 * canvas-shape import strategies (one file at a time, immediately added to
 * canvas), this collects ALL files first — a call chain needs every type
 * resolvable before traversal starts, and the entry point is picked by the
 * user only after parsing, not per file. Namespace is stamped onto each
 * type here (parseCsClasses attaches it once per file to the array, not
 * per type) so types from different files/namespaces can be merged safely.
 * @param {{name: string, source: string}[]} fileSources
 * @returns {{allParsedTypes: object[], entryPointOptions: {className: string, methodName: string}[], warnings: string[]}}
 */
export function parseAllForSequence(fileSources) {
  const warnings = [];
  const allParsedTypes = [];

  fileSources.forEach(function parseOneFile(file) {
    try {
      const parsed = parseCsClasses(file.source);
      parsed.forEach(function stampNamespace(type) {
        type.namespace = parsed.namespace;
      });
      allParsedTypes.push(...parsed);
    } catch (error) {
      warnings.push(`${file.name}: failed to parse (${error.message})`);
    }
  });

  const entryPointOptions = [];
  const seenKeys = new Set(); // dedupe (bug found 2026-07-19: a file dropped twice — e.g. dropped again on top of an earlier drop without clearing — duplicated every one of its methods in this list, causing React "duplicate key" warnings in the entry-method JxSelect)
  allParsedTypes.forEach(function collectEntryPoints(type) {
    type.members.forEach(function collectMethod(member) {
      if (member.memberKind === 'method' && member.bodyTokens) {
        const key = `${type.name}.${member.name}`;
        if (seenKeys.has(key)) return;
        seenKeys.add(key);
        entryPointOptions.push({ className: type.name, methodName: member.name });
      }
    });
  });

  return { allParsedTypes, entryPointOptions, warnings };
}

/**
 * Generates mermaid sequenceDiagram source for a chosen entry point, against
 * an already-parsed type set (see parseAllForSequence).
 * @param {object[]} allParsedTypes
 * @param {string} entryClassName
 * @param {string} entryMethodName
 * @param {object} [options]
 * @param {number} [options.maxDepth]
 * @param {Record<string,string>} [options.interfaceOverrides]
 * @returns {{mermaidSource: string, warnings: string[]}}
 */
export function generateSequenceMermaid(allParsedTypes, entryClassName, entryMethodName, options = {}) {
  const chain = walkCallChain(allParsedTypes, entryClassName, entryMethodName, options);
  const shape = buildSequenceShape(chain);
  return { mermaidSource: buildMermaidSequence(shape), warnings: chain.warnings };
}
